
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Employee, Project, Assignment } from './types';
import { parsePlainDateString } from '@/utils/dateTransforms';
import { parseAssignmentData, RawAssignmentData } from './parseAssignments';

export function useAssignments() {
  const queryClient = useQueryClient();

  const { data: profile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['userProfile'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const { data, error } = await supabase
        .from('users')
        .select('company_id')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error) throw error;
      return data;
    }
  });

  const { data: employees, isLoading: isLoadingEmployees } = useQuery({
    queryKey: ['employees', profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return [];

      const { data, error } = await supabase
        .from('employees')
        .select('id, name, role')
        .eq('company_id', profile.company_id)
        .order('name');

      if (error) throw error;
      return data as Employee[];
    },
    enabled: !!profile?.company_id,
  });

  const { data: projects, isLoading: isLoadingProjects } = useQuery({
    queryKey: ['projects', profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return [];

      const { data, error } = await supabase
        .from('projects')
        .select(`
          id,
          name,
          code,
          color,
          type,
          client:clients!inner (
            company_id
          )
        `)
        .eq('client.company_id', profile.company_id)
        .eq('status', 'active')
        .order('name');

      if (error) throw error;
      return data as Project[];
    },
    enabled: !!profile?.company_id,
  });



  const { data: assignments, isLoading: isLoadingAssignments, error: assignmentsError } = useQuery({
    queryKey: ['assignments', profile?.company_id],
    queryFn: async () => {
      if (!profile?.company_id) return [];

      const { data, error } = await supabase
        .from('employee_project_assignments')
        .select(`
          id,
          start_date,
          end_date,
          employee_id,
          hourly_rate,
          daily_hours,
          hour_overrides,
          isBillable,
          project:projects!inner (
            id,
            name,
            code,
            color,
            type,
            client:clients!inner (
              company_id
            )
          )
        `)
        .eq('project.client.company_id', profile.company_id)
        
        // Create the extended return type dinamically
        .returns<(RawAssignmentData & { 
          project: Project & {
              client: {
                company_id: string;
              }
            }
          })[]>();

      if (error) throw error;

      return parseAssignmentData(data);
    },
    enabled: !!profile?.company_id,
  });

  return {
    employees,
    projects,
    assignments,
    isLoading: isLoadingProfile || isLoadingEmployees || isLoadingProjects || isLoadingAssignments,
    error: assignmentsError,
    refetch: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['assignments'] });
    }
  };
}
